(() => {
  if (window.luxuryWishlistInstalled) return;
  window.luxuryWishlistInstalled = true;
  const key = 'luxuryWishlist';
  const buttons = '.luxury-wishlist-btn, .luxury-product-wishlist';
  const root = window.Shopify?.routes?.root || '/';
  const products = new Map();
  let loading = null;
  let catalogComplete = false;
  let renderVersion = 0;

  function read() {
    const value = JSON.parse(localStorage.getItem(key) || '[]');
    if (!Array.isArray(value)) throw new Error('Invalid wishlist data');
    return [...new Set(value.map(String))];
  }

  function sync() {
    const ids = read();
    document.querySelectorAll('#wishlist-count, .luxury-header-wishlist .wishlist-count').forEach(counter => {
      counter.textContent = String(ids.length);
    });
    document.querySelectorAll('.luxury-header-wishlist').forEach(link => {
      link.setAttribute('aria-label', `Wishlist, ${ids.length} items`);
    });
    document.querySelectorAll('.wishlist-icon').forEach(icon => {
      icon.style.color = ids.length ? '#c9a868' : '';
    });
    document.querySelectorAll(buttons).forEach(button => {
      const active = ids.includes(String(button.dataset.product));
      button.classList.toggle('active', active);
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
    });
    return ids;
  }

  function write(ids) {
    localStorage.setItem(key, JSON.stringify([...new Set(ids.map(String))]));
    sync();
    document.dispatchEvent(new CustomEvent('luxury:wishlist-change'));
  }

  // Fetch each catalogue page only once, rather than once per saved product.
  async function loadProducts() {
    if (catalogComplete) return;
    if (loading) return loading;
    loading = (async () => {
      for (let page = 1; ; page++) {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 15000);
        let data;
        try {
          const response = await fetch(`${root}products.json?limit=250&page=${page}`, { signal: controller.signal });
          if (!response.ok) throw new Error('Unable to load products');
          data = await response.json();
        } finally { clearTimeout(timer); }
        if (!Array.isArray(data.products)) throw new Error('Invalid product response');
        data.products.forEach(product => products.set(String(product.id), product));
        if (data.products.length < 250) break;
      }
      catalogComplete = true;
    })();
    try { await loading; } finally { loading = null; }
  }

  function message(grid, text) {
    const node = document.createElement('p');
    node.className = 'wishlist-empty';
    node.textContent = text;
    grid.replaceChildren(node);
  }

  async function render() {
    const grid = document.getElementById('luxuryWishlistGrid');
    if (!grid) return;
    const version = ++renderVersion;
    const ids = read();
    if (!ids.length) {
      grid.removeAttribute('aria-busy');
      message(grid, 'Your wishlist is empty.');
      return;
    }
    grid.setAttribute('aria-busy', 'true');
    if (!catalogComplete) message(grid, 'Loading your wishlist…');
    let failed = false;
    try { await loadProducts(); } catch (error) { failed = true; }
    if (version !== renderVersion || !grid.isConnected) return;
    grid.removeAttribute('aria-busy');
    const fragment = document.createDocumentFragment();
    ids.forEach(id => {
      const product = products.get(id);
      const card = document.createElement('article');
      card.className = 'wishlist-card';
      if (product) {
        const link = document.createElement('a');
        link.className = 'wishlist-card-link';
        link.href = `${root}products/${encodeURIComponent(product.handle)}`;
        if (product.images?.[0]?.src) {
          const image = document.createElement('img');
          image.src = product.images[0].src;
          image.alt = product.title;
          image.loading = 'lazy';
          link.append(image);
        }
        const title = document.createElement('div');
        title.className = 'wishlist-product-title';
        title.textContent = product.title;
        link.append(title);
        if (product.variants?.[0]?.price != null) {
          const price = document.createElement('div');
          price.className = 'wishlist-price';
          price.textContent = `₹${product.variants[0].price}`;
          link.append(price);
        }
        card.append(link);
      } else {
        const text = document.createElement('p');
        text.className = 'wishlist-product-title';
        text.textContent = failed ? 'This saved product could not be loaded. Please refresh to retry.' : 'This product is no longer available.';
        card.append(text);
      }
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'wishlist-remove';
      remove.dataset.wishlistRemove = id;
      remove.textContent = 'Remove';
      remove.setAttribute('aria-label', `Remove ${product?.title || 'saved product'} from wishlist`);
      card.append(remove);
      fragment.append(card);
    });
    grid.replaceChildren(fragment);
  }

  // Delegation includes recommendation and collection cards inserted later.
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const remove = event.target.closest('[data-wishlist-remove]');
    const button = event.target.closest(buttons);
    if (!remove && !button) return;
    event.preventDefault();
    event.stopPropagation();
    const id = remove?.dataset.wishlistRemove || button?.dataset.product;
    if (!id) return;
    if (!remove && !document.body.classList.contains('customer-logged-in')) {
      window.location.assign(`${root}account/login`);
      return;
    }
    try {
      const ids = read();
      write(remove || ids.includes(String(id)) ? ids.filter(value => value !== String(id)) : [...ids, String(id)]);
    } catch (error) {
      window.alert('Your wishlist could not be saved. Please check that browser storage is enabled and try again.');
    }
  }, true);

  document.addEventListener('luxury:wishlist-change', render);
  window.addEventListener('storage', event => {
    if (event.key === key || event.key === null) { sync(); render(); }
  });
  window.addEventListener('pageshow', () => { sync(); render(); });
  document.addEventListener('shopify:section:load', () => { sync(); render(); });

  function init() {
    try { sync(); render(); } catch (error) {
      const grid = document.getElementById('luxuryWishlistGrid');
      if (grid) message(grid, 'Your wishlist could not be loaded. Please check your browser storage settings.');
    }
    new MutationObserver(records => {
      if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches?.(`${buttons}, .luxury-header-wishlist`) || node.querySelector?.(`${buttons}, .luxury-header-wishlist`))))) sync();
    }).observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
